
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { Resource } from '@opentelemetry/resources';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { UserInteractionInstrumentation } from '@opentelemetry/instrumentation-user-interaction';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { ConfigService } from '../config.service';
import { trace, context, SpanStatusCode, Span } from '@opentelemetry/api';
import { OtelSpan } from '../../models/otel-span';
import { TelemetryFilter } from '../deduplication/telemetry-filter.service';

/**
 * Service responsible for initializing and configuring OpenTelemetry in the Angular application.
 * Uses configuration from ConfigService.
 */
@Injectable({
  providedIn: 'root'
})
export class TelemetryService implements OnDestroy {
  private static initialized = false; // Static for singleton pattern
  private initializationPromise: Promise<void> | null = null;
  private tracer: any = null; // Will hold the OpenTelemetry tracer
  private spans: Map<string, OtelSpan> = new Map();
  private tracerProvider: WebTracerProvider | null = null;
  private serviceName: string = ''; // Default empty, will be populated from config
  private exporter: OTLPTraceExporter | null = null; // Store exporter reference for shutdown
  private abortController: AbortController | null = null; // For cancelling in-flight requests

  constructor(
    private configService: ConfigService,
    private ngZone: NgZone,
    private telemetryFilter: TelemetryFilter
  ) {
    // Initialize with the service name from config
    this.serviceName = this.configService.getConfig().telemetry?.serviceName || 'finderwise-ui';

    // FIX FIN-0052: Cancel telemetry requests on page navigation to prevent HTTP/2 connection exhaustion
    // Problem: Telemetry POST requests hang during page unload, exhausting connection pool (6-10 max)
    // Result: New page can't load config.json → blank screen
    // Solution: Cancel all pending telemetry requests when page starts unloading
    window.addEventListener('pagehide', () => {
      console.log('[TelemetryService] Page hiding, cancelling pending telemetry requests');
      this.cancelPendingRequests();
    });
  }

  ngOnDestroy(): void {
    this.cancelPendingRequests();
  }

  /**
   * Initialize telemetry after configuration is loaded
   * @returns A promise that resolves when initialization is complete
   */
  public initialize(): Promise<void> {
    // Singleton pattern - prevent duplicate initialization
    if (TelemetryService.initialized) {
      console.warn('[TelemetryService] Duplicate initialization detected - skipping');
      return Promise.resolve();
    }

    // Return the existing promise if initialization is in progress
    if (this.initializationPromise) {
      return this.initializationPromise;
    }
    
    // Initialize and store the promise - running outside of Angular's zone
    // to prevent blocking the UI rendering
    this.initializationPromise = this.ngZone.runOutsideAngular(() => 
      this.initializeTracing()
    ).finally(() => {
      // Clear the promise reference after completion
      this.initializationPromise = null;
    });
    
    return this.initializationPromise;
  }

  /**
   * Initialize OpenTelemetry tracing with proper configurations.
   * @returns Promise that resolves when initialization is complete
   */
  private async initializeTracing(): Promise<void> {
    try {
      const config = this.configService.getConfig();
      const telemetryConfig = config.telemetry;
      
      // Skip initialization if telemetry is disabled or already initialized
      if (TelemetryService.initialized || typeof window === 'undefined' || !telemetryConfig?.enabled) {
        console.debug('Skipping telemetry initialization - disabled or already initialized');
        return;
      }
      
      // Use microtask scheduling to yield to more important rendering tasks
      await Promise.resolve();
      
      // Make sure we have the service name from config
      this.serviceName = telemetryConfig.serviceName || 'finderwise-ui';
      console.debug('Using telemetry service name:', this.serviceName);
      
      // Get the collector URL for sending traces
      const collectorUrl = this.getCollectorUrl(telemetryConfig, config);
      console.debug('Initializing OpenTelemetry with collector URL:', collectorUrl);

      // Create AbortController for cancelling requests during page navigation
      this.abortController = new AbortController();

      // Create and configure the trace exporter with proper content type and timeout
      // FIX FIN-0052: Add timeout and signal to prevent hanging requests
      this.exporter = new OTLPTraceExporter({
        url: collectorUrl,
        headers: {
          'Content-Type': 'application/json'
        },
        timeoutMillis: 5000, // 5 second timeout for telemetry requests
      });
      
      // Create a tracer provider with appropriate resource information
      const provider = new WebTracerProvider({
        resource: new Resource({
          [SemanticResourceAttributes.SERVICE_NAME]: this.serviceName,
          [SemanticResourceAttributes.SERVICE_VERSION]: telemetryConfig.serviceVersion || '1.0.0',
          'environment': telemetryConfig.environment || 'development',
        }),
      });
      
      // Store the provider for later use
      this.tracerProvider = provider;

      // Use batch span processor with the OTLP exporter for better performance
      // FIX FIN-0052: Reduce batch delay for faster export during page navigation
      provider.addSpanProcessor(new BatchSpanProcessor(this.exporter, {
        // Use more aggressive batching for better performance
        maxExportBatchSize: 20,
        scheduledDelayMillis: 500, // Reduced from 1000ms to export faster before page unload
        maxQueueSize: 100 // Limit queue size to prevent memory buildup
      }));
      
      // Register the provider
      provider.register({
        contextManager: new ZoneContextManager(),
      });
      
      // Register automatic instrumentations - with reduced options
      registerInstrumentations({
        instrumentations: [
          // Instrument document load events
          new DocumentLoadInstrumentation(),
          
          // Only instrument critical HTTP requests
          new FetchInstrumentation({
            propagateTraceHeaderCorsUrls: [/.*/], // Propagate headers for all URLs
            clearTimingResources: true,
            ignoreUrls: [/assets\/.*/, /telemetry\/.*/], // Ignore asset and telemetry requests
          }),
          
          // Only instrument critical XMLHttpRequests
          new XMLHttpRequestInstrumentation({
            propagateTraceHeaderCorsUrls: [/.*/], // Propagate headers for all URLs
            ignoreUrls: [/assets\/.*/, /telemetry\/.*/], // Ignore asset and telemetry requests
          }),

          // Add user interaction instrumentation
          new UserInteractionInstrumentation({
            eventNames: ['click', 'mousedown', 'touchstart', 'keydown'],
            shouldPreventSpanCreation: (eventType: string, element: EventTarget | null, span: any) => {
              // Prevent span creation for keydown events except in text areas
              return eventType === 'keydown' && !(element instanceof HTMLTextAreaElement);
            }
          })
        ],
      });

      // Get the tracer for our application - use the same serviceName consistently
      this.tracer = trace.getTracer(this.serviceName);

      TelemetryService.initialized = true;
      console.debug('OpenTelemetry initialized successfully with service name:', this.serviceName);
    } catch (error) {
      console.error('Failed to initialize OpenTelemetry', error);
    }
  }

  /**
   * Gets the URL of the OpenTelemetry collector to send traces to.
   * This method ensures the URL works with proxies in development and direct calls in production.
   */
  public getCollectorUrl(telemetryConfig: any, config: any): string {
    // Use the configured collector URL or default
    let collectorUrl = telemetryConfig?.collectorUrl || '/telemetry/v1/traces';
    
    // Ensure the URL starts with a slash if it's a relative path
    if (!collectorUrl.startsWith('/')) {
      collectorUrl = '/' + collectorUrl;
    }
    
    // For absolute URLs (with protocol), use as-is
    if (collectorUrl.startsWith('http')) {
      return collectorUrl;
    }
    
    // For relative URLs, prepend the origin
    return `${window.location.origin}${collectorUrl}`;
  }

  /**
   * Track an event using OpenTelemetry
   * @param eventName The name of the event to track
   * @param attributes Additional attributes to record with the event
   * @param traceId Optional traceId to use as parent for the span
   * @param spanId Optional spanId to use as parent for the span
   */
  public trackEvent(eventName: string, attributes: Record<string, any> = {}, traceId?: string, spanId?: string): void {
    // Skip if not initialized
    if (!TelemetryService.initialized || !this.tracer) {
      // Log the event for development purposes even if telemetry isn't initialized
      if (!this.configService.getConfig().production) {
        console.debug('Telemetry event (not sent):', eventName, attributes);
      }
      return;
    }

    // Apply intelligent filtering to reduce duplicate and zero-information events
    if (!this.telemetryFilter.trackEvent(eventName, attributes)) {
      // Event was filtered out - no need to create a span
      return;
    }

    try {
      let span;
      if (traceId && spanId && traceId.length === 32 && spanId.length === 16) {
        // Create a parent SpanContext from the traceId and spanId
        const parentSpanContext = {
          traceId: traceId,
          spanId: spanId,
          traceFlags: 1,
          isRemote: true
        };
        const parentContext = trace.setSpanContext(context.active(), parentSpanContext);
        span = this.tracer.startSpan(`Event.${eventName}`, undefined, parentContext);
      } else if (traceId && traceId.length === 32) {
        // Fallback: only traceId
        const parentSpanContext = {
          traceId: traceId,
          spanId: '0000000000000001', // dummy spanId, not used as real parent
          traceFlags: 1,
          isRemote: true
        };
        const parentContext = trace.setSpanContext(context.active(), parentSpanContext);
        span = this.tracer.startSpan(`Event.${eventName}`, undefined, parentContext);
      } else {
        span = this.tracer.startSpan(`Event.${eventName}`);
      }

      const spanContext = span.spanContext();

      // Add the attributes to the span
      Object.entries(attributes).forEach(([key, value]) => {
        const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
        span.setAttribute(key, stringValue);
      });

      // Store minimal span info for debugging (don't compete with OTel timing)
      const otelSpan: OtelSpan = {
        traceId: spanContext.traceId,
        spanId: spanContext.spanId,
        name: `Event.${eventName}`,
        startTime: performance.now(), // Only for local debugging
        kind: 'INTERNAL',
        attributes: Object.fromEntries(
          Object.entries(attributes).map(([key, value]) =>
            [key, typeof value === 'object' ? JSON.stringify(value) : String(value)]
          )
        )
      };

      this.spans.set(otelSpan.spanId, otelSpan);

      // End the span immediately - OpenTelemetry handles all timing
      span.end();
    } catch (error) {
      console.warn('Failed to track event:', error);
    }
  }

  /**
   * Track an error using OpenTelemetry
   * @param errorName The name/category of the error
   * @param attributes Additional attributes to record with the error
   */
  public trackError(errorName: string, attributes: Record<string, any> = {}): void {
    /* ------------------------------------------------------------------
     * Telemetry not ready yet - skip cleanly in ALL environments.
     * Avoid accessing ConfigService before it is initialised.
     * --------------------------------------------------------------- */
    if (!TelemetryService.initialized || !this.tracer) {
      console.debug('Telemetry error (not sent):', errorName, attributes);
      return;
    }

    try {
      // Create a span for the error
      const span = this.tracer.startSpan(`Error.${errorName}`);

      // Add the attributes to the span
      Object.entries(attributes).forEach(([key, value]) => {
        // Convert values to string to ensure compatibility
        span.setAttribute(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      });

      // Set span status to ERROR
      span.setStatus({
        code: SpanStatusCode.ERROR,
        message: attributes['error'] || errorName
      });

      // End the span - let OpenTelemetry handle timing automatically
      span.end();
    } catch (error) {
      console.warn('Failed to track error:', error);
    }
  }

  /**
   * Record a metric value using OpenTelemetry
   * @param metricName The name of the metric to record
   * @param value The numeric value to record
   * @param attributes Additional attributes to record with the metric
   */
  public recordMetric(metricName: string, value: number, attributes: Record<string, any> = {}): void {
    // Skip if not initialized
    if (!TelemetryService.initialized || !this.tracer) {
      if (!this.configService.getConfig().production) {
        console.debug('Telemetry metric (not sent):', metricName, value, attributes);
      }
      return;
    }

    try {
      // Create a span for the metric
      const span = this.tracer.startSpan(`Metric.${metricName}`);
      
      // Add the metric value as an attribute
      span.setAttribute('metric.value', value);
      span.setAttribute('metric.name', metricName);
      
      // Add any additional attributes
      Object.entries(attributes).forEach(([key, value]) => {
        span.setAttribute(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      });
      
      // End the span immediately
      span.end();
    } catch (error) {
      console.warn('Failed to record metric:', error);
    }
  }

  /**
   * Cancel all pending telemetry requests immediately
   * FIX FIN-0052: Prevent hanging telemetry requests from blocking HTTP/2 connection pool
   */
  private cancelPendingRequests(): void {
    try {
      // Abort any in-flight HTTP requests
      if (this.abortController) {
        console.debug('[TelemetryService] Aborting in-flight telemetry requests');
        this.abortController.abort();
        this.abortController = new AbortController(); // Create new one for future requests (if any)
      }

      // Shutdown the exporter to stop all queued exports
      if (this.exporter && typeof (this.exporter as any).shutdown === 'function') {
        console.debug('[TelemetryService] Shutting down exporter');
        // Shutdown is synchronous in most cases, fire and forget
        void (this.exporter as any).shutdown();
      }

      // Shutdown the tracer provider to stop the batch processor
      if (this.tracerProvider && typeof this.tracerProvider.shutdown === 'function') {
        console.debug('[TelemetryService] Shutting down tracer provider');
        // Shutdown is synchronous, fire and forget
        void this.tracerProvider.shutdown();
      }
    } catch (error) {
      // Silently catch - telemetry cleanup should never throw
      console.debug('[TelemetryService] Error during cleanup (non-critical):', error);
    }
  }

  /**
   * Force flush all pending spans (for beforeunload)
   * DEPRECATED: This approach causes hanging requests. Use cancelPendingRequests() instead via pagehide.
   *
   * FIX FIN-0052: This method is kept for backward compatibility but should not be used.
   * The forceFlush() on beforeunload was causing telemetry requests to hang during navigation,
   * exhausting HTTP/2 connection pool and blocking config.json loading on the new page.
   *
   * New approach: Cancel requests immediately on pagehide instead of flushing.
   */
  public forceFlush(): Promise<void> {
    console.warn('[TelemetryService] forceFlush() is deprecated. Use cancelPendingRequests() on pagehide instead.');
    if (this.tracerProvider && typeof this.tracerProvider.forceFlush === 'function') {
      return this.tracerProvider.forceFlush();
    }
    return Promise.resolve();
  }

  /**
   * Gets the current service name being used for telemetry
   * @returns The service name string
   */
  public getServiceName(): string {
    return this.serviceName;
  }

  /**
   * Gets the configured tracer instance for manual span creation
   * @returns The OpenTelemetry tracer instance
   */
  public getTracer(): any {
    if (!TelemetryService.initialized || !this.tracer) {
      console.warn('Telemetry not initialized, manual spans will not be exported');
      return null;
    }
    return this.tracer;
  }
}